"use client";

import { useEffect } from "react";

export function useGlobalErrorHandler() {
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      // Ignore Next.js internal errors - these are not actual errors
      if (
        event.error?.message === "NEXT_REDIRECT" ||
        event.error?.message === "NEXT_NOT_FOUND" ||
        event.error?.digest?.startsWith("NEXT")
      ) {
        return;
      }

      console.error("Global error caught:", event.error);

      // Check if it's a hooks violation error
      if (
        event.error?.message?.includes(
          "more hooks than during the previous render",
        )
      ) {
        console.error("Hooks violation detected, attempting recovery...");

        // Try to recover by reloading after a short delay
        setTimeout(() => {
          window.location.reload();
        }, 30000);

        // Prevent the error from crashing the app
        event.preventDefault();
        return false;
      }

      // Log other errors but don't reload
      if (process.env.NODE_ENV === "development") {
        console.error("Unhandled error:", {
          message: event.error?.message,
          stack: event.error?.stack,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        });
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      // Ignore Next.js internal errors - these are not actual errors
      if (
        event.reason?.message === "NEXT_REDIRECT" ||
        event.reason?.message === "NEXT_NOT_FOUND" ||
        event.reason?.digest?.startsWith("NEXT")
      ) {
        return;
      }

      console.error("Unhandled promise rejection:", event.reason);

      if (process.env.NODE_ENV === "development") {
        console.error("Promise rejection details:", {
          reason: event.reason,
          stack: event.reason?.stack,
        });
      }

      // Prevent the unhandled rejection from crashing the app
      event.preventDefault();
    };

    // Add global error listeners
    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection,
      );
    };
  }, []);
}

export default useGlobalErrorHandler;
